// Refactored from https://github.com/Azure-Samples/ms-identity-javascript-react-tutorial/blob/main/1-Authentication/1-sign-in/SPA/src/authConfig.js

import { IPublicClientApplication } from "@azure/msal-browser";

// App Services endpoints intentionally not used for Container Apps / custom backends
const appServicesAuthLogoutUrl = ".auth/logout?post_logout_redirect_uri=/";

interface AppServicesToken {
    id_token: string;
    access_token: string;
    user_claims: Record<string, any>;
    expires_on: string;
}

interface AuthSetup {
    // Set to true if login elements should be shown in the UI
    useLogin: boolean;
    // Set to true if access control is enforced by the application
    requireAccessControl: boolean;
    // Set to true if the application allows unauthenticated access (only applies for documents without access control)
    enableUnauthenticatedAccess: boolean;
    /**
     * Configuration object to be passed to MSAL instance on creation.
     * For a full list of MSAL.js configuration parameters, visit:
     * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
     */
    msalConfig: {
        auth: {
            clientId: string; // Client app id used for login
            authority: string; // Directory to use for login https://learn.microsoft.com/entra/identity-platform/msal-client-application-configuration#authority
            redirectUri: string; // Points to window.location.origin. You must register this URI on Azure Portal/App Registration.
            postLogoutRedirectUri: string; // Indicates the page to navigate after logout.
            navigateToLoginRequestUrl: boolean; // If "true", will navigate back to the original request location before processing the auth code response.
        };
        cache: {
            cacheLocation: string; // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
            storeAuthStateInCookie: boolean; // Set this to "true" if you are having issues on IE11 or Edge
        };
    };
    loginRequest: {
        /**
         * Scopes you add here will be prompted for user consent during sign-in.
         * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
         * For more information about OIDC scopes, visit:
         * https://learn.microsoft.com/entra/identity-platform/permissions-consent-overview#openid-connect-scopes
         */
        scopes: Array<string>;
    };
    tokenRequest: {
        scopes: Array<string>;
    };
}

// Fetch the auth setup JSON data from the API if not already cached
export async function fetchAuthSetup(): Promise<AuthSetup> {
    const response = await fetch("/auth_setup");
    if (!response.ok) {
        throw new Error(`auth setup response was not ok: ${response.status}`);
    }
    return await response.json();
}

let authSetup: AuthSetup;

export async function initializeAuthSetup() {
    if (!authSetup) {
        authSetup = await fetchAuthSetup();
    }
}

export const getAuthSetup = () => authSetup;

export const useLogin = () => authSetup?.useLogin;
export const requireAccessControl = () => authSetup?.requireAccessControl;

export const enableUnauthenticatedAccess = () => authSetup?.enableUnauthenticatedAccess;

export const requireLogin = () => requireAccessControl() && !enableUnauthenticatedAccess();

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig = () => authSetup.msalConfig;

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://learn.microsoft.com/entra/identity-platform/permissions-consent-overview#openid-connect-scopes
 */
export const loginRequest = () => authSetup.loginRequest;

const tokenRequest = () => authSetup.tokenRequest;

// Build an absolute redirect URI using the current window's location and the relative redirect URI from auth setup
export const getRedirectUri = () => {
    // If using HashRouter, the redirect URI needs to include the hash
    // If using BrowserRouter, the redirect URI should not include the hash
    // We are currently using BrowserRouter, so this will resolve to window.location.origin + "/redirect"
    const isHashRouter = window.location.hash.includes("#/");
    return window.location.origin + (isHashRouter ? "/#/" : "") + authSetup.msalConfig.auth.redirectUri;
};

// Disable App Services auth detection for Container Apps / custom backends.
// Do NOT call /.auth/me or attempt App Services token flows from the frontend.
export const isUsingAppServicesLogin = false;

// Sign out of app services
// Learn more at https://learn.microsoft.com/azure/app-service/configure-authentication-customize-sign-in-out#sign-out-of-a-session
export const appServicesLogout = () => {
    window.location.href = appServicesAuthLogoutUrl;
};

/**
 * Determines if the user is logged in either via the MSAL public client application or the app services login.
 * @param {IPublicClientApplication | undefined} client - The MSAL public client application instance, or undefined if not available.
 * @returns {Promise<boolean>} A promise that resolves to true if the user is logged in, false otherwise.
 */
export const checkLoggedIn = async (client: IPublicClientApplication | undefined): Promise<boolean> => {
    // Only consider MSAL client state for login detection in Container Apps / custom backends.
    if (client) {
        return client.getActiveAccount() != null;
    }
    return false;
};

// Get an access token for use with the API server.
// ID token received when logging in may not be used for this purpose because it has the incorrect audience
export const getToken = async (client: IPublicClientApplication): Promise<string | null> => {
    if (!client) return null;
    try {
        const result = await client.acquireTokenSilent({
            ...tokenRequest(),
            redirectUri: getRedirectUri()
        });
        return result?.accessToken ?? null;
    } catch (error) {
        console.log("acquireTokenSilent failed:", error);
        return null;
    }
};

/**
 * Retrieves the username of the active account.
 * If no active account is found, attempts to retrieve the username from the app services login token if available.
 * @param {IPublicClientApplication} client - The MSAL public client application instance.
 * @returns {Promise<string | null>} The username of the active account, or null if no username is found.
 */
export const getUsername = async (client: IPublicClientApplication): Promise<string | null> => {
    // Check for local user token first
    const localToken = localStorage.getItem("localToken");
    if (localToken) {
        try {
            const payload = JSON.parse(atob(localToken.split(".")[1]));
            if (payload.exp * 1000 > Date.now()) {
                return payload.name || "Local User";
            }
        } catch (e) {
            console.error("Error decoding local token", e);
        }
    }

    if (!client) return null;
    const activeAccount = client.getActiveAccount();
    return activeAccount ? activeAccount.username.toLowerCase() : null;
};

/**
 * Retrieves the token claims of the active account.
 * If no active account is found, attempts to retrieve the token claims from the app services login token if available.
 * @param {IPublicClientApplication} client - The MSAL public client application instance.
 * @returns {Promise<Record<string, unknown> | undefined>} A promise that resolves to the token claims of the active account, the user claims from the app services login token, or undefined if no claims are found.
 */
export const getTokenClaims = async (client: IPublicClientApplication): Promise<Record<string, unknown> | undefined> => {
    if (!client) return undefined;
    const activeAccount = client.getActiveAccount();
    return activeAccount ? activeAccount.idTokenClaims : undefined;
};
